import { formatNumber } from '@rhino-project/core/i18n/format';
import { useTranslation } from '@rhino-project/core/i18n';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@iconify/react';
import RatingScore from './RatingScore';
import StarMeter from './StarMeter';
import { summarizeReport } from './ratingSummary';
import type { RhinoRead } from '@rhino-project/core';
import { formatActivityDate } from '../../utils/dates';

type Report = RhinoRead<'report'>;
type ReportValue = Report['report_values'][number];

type ReportListValue = {
  id: ReportValue['id'];
  value: ReportValue['value'];
  report_field?: Partial<ReportValue['report_field']> | null;
};

type ReportListEntry = Pick<Report, 'id' | 'reported_at' | 'observing'> & {
  report_values?: ReportListValue[] | null;
  report_type?: Partial<Report['report_type']> | null;
  reporter?: Partial<Report['reporter']> | null;
};

const fieldName = (rv: ReportListValue) => {
  const f = rv.report_field;
  return f?.display_name ?? f?.name ?? '';
};

interface ReportListItemProps {
  report: ReportListEntry;
  owner: string;
}

const ReportListItem = ({ report, owner }: ReportListItemProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const summary = summarizeReport(report);
  const reportType = report.report_type;
  const reporter = report.reporter;

  const values = report.report_values ?? [];
  const notes = values.filter(
    (rv) => rv.report_field?.field_type === 'text' && rv.value
  );
  // Preview prefers the per-player "Note", falling back to any other text field
  // (e.g. the shared "Game Note").
  const previewNote =
    notes.find((rv) => fieldName(rv).toLowerCase() === 'note') ?? notes[0];
  const previewText = previewNote?.value ?? '';

  const observing =
    typeof report.observing === 'string'
      ? report.observing.replace(/_/g, ' ')
      : null;

  const tileLabel = summary.showGrades
    ? t('frontend.messages.avg')
    : summary.label;

  return (
    <div
      className="flex items-start gap-3 rounded-lg border-t border-separator px-2 py-3 first:border-t-0 cursor-pointer hover:bg-foreground/[0.03]"
      onClick={() => setOpen((o) => !o)}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen((o) => !o);
        }
      }}
    >
      {/* Rating tile — fixed anchor, pinned to the top of the row */}
      <div className="flex w-[68px] shrink-0 flex-col items-center rounded-lg bg-surface-secondary px-1 py-2">
        <RatingScore value={summary.value} label={tileLabel} size="sm" />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium">
            <Link
              to="/$owner/reports/$id"
              params={{ owner, id: String(report.id) }}
              onClick={(e) => e.stopPropagation()}
              className="hover:text-accent"
            >
              {reportType?.display_name}
            </Link>
          </span>
          <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted tabular-nums">
            {formatActivityDate(report.reported_at as string)}
          </span>
        </div>

        <div className="mt-0.5 text-sm text-muted">
          <span>{reporter?.display_name as string}</span>
          {observing && (
            <>
              <span className="mx-1.5 opacity-50">·</span>
              <span className="capitalize">{observing}</span>
            </>
          )}
        </div>

        <div className="mt-1.5 flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm ${
              previewText ? 'text-foreground/80' : 'italic text-muted'
            }`}
          >
            {previewText || t('frontend.messages.no_note')}
          </span>
          <Icon
            icon="bi:chevron-down"
            width="14"
            className={`shrink-0 text-muted transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>

        {open && (
          <div className="mt-3 flex flex-col gap-3.5">
            {summary.showGrades && (
              <div className="flex flex-col">
                {summary.grades.map((g, i) => (
                  <div
                    key={g.id as number}
                    className={`flex items-center justify-between gap-3 py-1.5 ${
                      i > 0 ? 'border-t border-separator' : ''
                    }`}
                  >
                    <span className="text-sm">{g.name}</span>
                    <div className="flex items-center gap-2.5">
                      <StarMeter value={g.value} className="text-xs" />
                      <span className="w-6 text-right text-sm font-semibold tabular-nums text-muted">
                        {formatNumber(g.value, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {notes.map((rv) => (
              <div key={rv.id as number}>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                  {fieldName(rv)}
                </div>
                <p className="whitespace-pre-line wrap-break-word border-l-2 border-accent/40 pl-3 text-sm leading-relaxed">
                  {rv.value as string}
                </p>
              </div>
            ))}
            {notes.length === 0 && (
              <div className="text-sm text-muted">
                {t('frontend.messages.no_note_2')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportListItem;
